import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import useGlobalStore from 'hooks/useGlobalStore';

/**
 * Hook for handling products modal opening and closing logic
 *
 * @param {Object} [data] - Carousel data containing items (required for opening modal)
 * @param {number} [maxCarouselItems=24] - Maximum number of carousel items to display before showing "See All"
 * @returns {Object} Modal handlers and state
 * @returns {Function} returns.handleOpenProductsModal - Function to open the products modal. Accepts openOptions: { isModalDeepLink?: boolean, productCardMetaData?: Object }. No-op if data is not provided.
 * @returns {Function} returns.handleCloseProductsModal - Function to close the products modal and clear data
 * @returns {boolean} returns.productsModalOpen - Current open state of the modal
 * @returns {Object|null} returns.productsModalData - Current modal data (null when modal is closed)
 */
function useProductsModal(data, maxCarouselItems = 24) {
    const { productsModal, productsModalDataRef, setProductsModal } = useGlobalStore();
    const location = useLocation();

    // Clear modal data when modal closes (but keep source route for restoration)
    useEffect(() => {
        if (!productsModal.open) {
            productsModalDataRef.current = null;
        }
    }, [productsModal.open]);

    const handleOpenProductsModal = (openOptions = {}) => {
        if (!data) {
            return;
        }

        const deepLinkProductID = openOptions.isModalDeepLink
            ? (data?.items?.[maxCarouselItems - 1]?.id ?? null)
            : null;

        const modalData = {
            data,
            deepLinkProductID,
            productCardMetaData: openOptions.productCardMetaData || {},
        };

        productsModalDataRef.current = modalData;

        setProductsModal({
            open: true,
            sourceRoute: location.pathname,
        });
    };

    const handleCloseProductsModal = () => {
        setProductsModal({
            open: false,
            sourceRoute: null,
        });
        productsModalDataRef.current = null;
    };

    // Get current modal data when modal is open (read from ref to avoid unnecessary re-renders)
    const productsModalData = productsModal.open ? productsModalDataRef.current : null;

    return {
        handleOpenProductsModal,
        handleCloseProductsModal,
        productsModalOpen: productsModal.open,
        productsModalData,
    };
}

/**
 * Hook for cleaning up products modal state and restoring modal when returning from product page
 *
 * Modal data persists across navigations to product pages and is only cleared when:
 * - User explicitly closes the modal
 * - User navigates away from the source route (except when navigating to product pages)
 */
function useProductsModalCleanup() {
    const { productsModal, productsModalDataRef, setProductsModal } = useGlobalStore();

    const location = useLocation();
    const isShopProductRoute = location.pathname.startsWith('/shop/product');

    useEffect(() => {
        const modalData = productsModalDataRef.current;
        const currentPath = location.pathname;

        // Restore modal if:
        // 1. We have modal data in store (it was kept alive)
        // 2. We're back on the source route where modal was opened
        // 3. Modal is not currently open
        // 4. We're not on a product page (we just navigated back from one)
        const shouldRestoreModal =
            modalData && productsModal.sourceRoute === currentPath && !productsModal.open && !isShopProductRoute;

        if (shouldRestoreModal) {
            setProductsModal((prev) => ({ ...prev, open: true }));
        }

        // Cleanup: Clear modal data and source route when navigating away from source route
        // (but keep it alive when navigating to product pages)
        if (productsModal.sourceRoute && productsModal.sourceRoute !== currentPath && !isShopProductRoute) {
            productsModalDataRef.current = null;
            setProductsModal({
                open: false,
                sourceRoute: null,
            });
        }
    }, [location.pathname, productsModal, isShopProductRoute, setProductsModal]);
}

export default useProductsModal;

export { useProductsModalCleanup };
